import React, { createContext, useContext, useState, useEffect } from 'react';
import { publicSettingsAPI } from '../services/api';

const SiteSettingsContext = createContext();

export const useSiteSettings = () => {
  const context = useContext(SiteSettingsContext);
  if (!context) {
    throw new Error('useSiteSettings must be used within a SiteSettingsProvider');
  }
  return context;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(null); // Start with null instead of defaults
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSiteSettings = async () => {
    try {
      setLoading(true);
      const __debug = typeof window !== 'undefined' && (window.localStorage?.DEBUG === 'true' || window.localStorage?.DEBUG === '1');
      if (__debug) console.log('🔍 Fetching site settings...');
      const response = await publicSettingsAPI.getSettings();
      if (__debug) console.log('🔍 Settings response:', response);
      
      if (response.data && response.data.data && response.data.data.settings) {
        const backendSettings = response.data.data.settings;
        if (__debug) console.log('🔍 Backend settings:', backendSettings);
        
        // Extract site settings from backend with proper null checks
        const siteSettings = {
          siteTitle: backendSettings.site_title?.value || 'Niswa Info Tech',
          siteTagline: backendSettings.site_tagline?.value || 'Leading Digital Agency for Web Development, Mobile Apps & Digital Marketing',
          logoLight: backendSettings.logo_light?.value || '',
          logoDark: backendSettings.logo_dark?.value || '',
          favicon: backendSettings.favicon?.value || '',
          enableDarkMode: backendSettings.enable_dark_mode?.value !== false // Default to true if not set
        };
        
        if (__debug) console.log('🔍 Processed site settings:', siteSettings);
        setSettings(siteSettings);
        
        // Update document title and favicon
        if (siteSettings.siteTitle) {
          document.title = siteSettings.siteTitle;
        }
        
        if (siteSettings.favicon) {
          // Remove existing favicon links
          const existingFavicons = document.querySelectorAll("link[rel*='icon']");
          existingFavicons.forEach(link => link.remove());
          
          // Add new favicon
          const faviconLink = document.createElement('link');
          faviconLink.type = 'image/x-icon';
          faviconLink.rel = 'shortcut icon';
          faviconLink.href = siteSettings.favicon;
          document.getElementsByTagName('head')[0].appendChild(faviconLink);
          
          // Also update meta description if available
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription && siteSettings.siteTagline) {
            metaDescription.setAttribute('content', siteSettings.siteTagline);
          }
        }
      } else {
        if (__debug) console.log('🔍 No settings found in response, using defaults');
        if (__debug) console.log('🔍 Response structure:', response);
        
        // Set default settings if no backend settings found
        const defaultSettings = {
          siteTitle: 'Niswa Info Tech',
          siteTagline: 'Leading Digital Agency for Web Development, Mobile Apps & Digital Marketing',
          logoLight: '',
          logoDark: '',
          favicon: '',
          enableDarkMode: true
        };
        setSettings(defaultSettings);
      }
    } catch (err) {
      console.error('❌ Error fetching site settings:', err);
      setError(err.message);
      
      // Set default settings on error
      const defaultSettings = {
        siteTitle: 'Niswa Info Tech',
        siteTagline: 'Leading Digital Agency for Web Development, Mobile Apps & Digital Marketing',
        logoLight: '',
        logoDark: '',
        favicon: '',
        enableDarkMode: true
      };
      setSettings(defaultSettings);
    } finally {
      setLoading(false);
    }
  };

  const getLogoUrl = (isDarkMode = false) => {
    if (!settings) return '';
    
    try {
      if (isDarkMode && settings.logoDark) {
        return settings.logoDark;
      }
      if (!isDarkMode && settings.logoLight) {
        return settings.logoLight;
      }
      // Fallback to light logo if dark logo is not available
      return settings.logoLight || '';
    } catch (error) {
      console.error('❌ Error in getLogoUrl:', error);
      return '';
    }
  };

  const updateSettings = async (newSettings) => {
    try {
      // For now, we'll just refresh the settings after a delay
      // In a real implementation, you might want to call the admin API here
      setTimeout(() => {
        fetchSiteSettings();
      }, 1000);
      return true;
    } catch (err) {
      console.error('Error updating site settings:', err);
      return false;
    }
  };

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const value = {
    settings,
    loading,
    error,
    fetchSiteSettings,
    updateSettings,
    getLogoUrl
  };

  return (
    <SiteSettingsContext.Provider value={value}>
      {children}
    </SiteSettingsContext.Provider>
  );
}; 